import { CollectionSkeleton } from '@/components/features/Collection/CollectionsGrid/CollectionSkeleton';
import CollectionsGrid from '@/components/features/Collection/CollectionsGrid/CollectionsGrid';
import { PageHeader } from '@/components/shared/PageHeader';
import { SocialMediaLinks } from '@/components/shared/SocialMediaLinks';
import { getCollectionsInfinite, useCollectionsInfinite } from '@/hooks/use-collections-infinite';
import { useMarketplaceChain } from '@/hooks/use-marketplace-chain';
import { getSSRChain } from '@/lib/reservoir/get-ssr-chain';
import { GetCollectionsV7SortBy } from '@/lib/reservoir/reservoir.generated';
import { routes } from '@/utils/routes';
import { Button, Container, SimpleGrid } from '@chakra-ui/react';
import { isDefined } from '@onbeam/utils';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import { GetServerSideProps, type NextPage } from 'next';
import { NextSeo } from 'next-seo';
import useTranslation from 'next-translate/useTranslation';
import { rem } from 'polished';

export const collectionsInfiniteSortBy: GetCollectionsV7SortBy = 'allTimeVolume';

const CollectionsPage: NextPage<Props> = () => {
  const { t } = useTranslation('collection-overview');
  const chain = useMarketplaceChain();

  const { data, isLoading, fetchNextPage, isFetchingNextPage, hasNextPage } =
    useCollectionsInfinite({
      params: { sortBy: collectionsInfiniteSortBy },
    });

  const collections = data?.pages?.flatMap(page => page.collections).filter(isDefined);

  return (
    <>
      <NextSeo title={t('page-title')} description={t('page-description')} />

      <PageHeader
        containerVariant="breakout"
        title={t('page-title')}
        pages={[
          {
            name: t('common:crumbs.home'),
            href: routes.home(chain.routePrefix),
          },
          {
            name: t('page-title'),
            href: routes.collections.overview(chain.routePrefix),
          },
        ]}
      />

      <Container
        variant="breakout"
        display="flex"
        flexDir="column"
        alignItems="center"
        gap="space.56"
        mb="space.80"
      >
        {isLoading ? (
          <SimpleGrid w="full" columns={{ base: 1, md: 3, '2xl': 4 }} spacing="space.24">
            {[...Array(20)].map((_, index) => (
              <CollectionSkeleton key={index} />
            ))}
          </SimpleGrid>
        ) : (
          <CollectionsGrid collections={collections} />
        )}
        {hasNextPage && !!collections?.length && (
          <Button
            variant="secondary"
            onClick={() => fetchNextPage()}
            isLoading={isFetchingNextPage}
          >
            {t('load-more')}
          </Button>
        )}
      </Container>
      <SocialMediaLinks mt={{ base: 'space.88', md: rem(200) }} />
    </>
  );
};

type Params = {
  chain: string;
};

type Props = {
  dehydratedState: unknown;
};

export const getServerSideProps: GetServerSideProps<Props, Params> = async ctx => {
  const SSRClient = new QueryClient();
  const chain = getSSRChain(ctx);

  await SSRClient.prefetchInfiniteQuery(
    getCollectionsInfinite(chain, {
      params: { sortBy: collectionsInfiniteSortBy },
    }),
  );

  return {
    props: {
      dehydratedState: dehydrate(SSRClient),
    },
  };
};

export default CollectionsPage;
